import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { usePopoverDismissOutside } from './usePopoverDismissOutside';
import { Loader2, Share2, X } from 'lucide-react';
import type { KanzleiSeatForAktePicker } from './portalAktenTypes';
import type { PortalProtokoll } from './portalDocuments';
import {
  SHARE_POPOVER_WIDTH_PX,
  createPortalDocumentShare,
  deletePortalDocumentShare,
  describePortalDocumentVisibility,
  portalShareSeatLabel,
  shareTargetSeats,
  type PortalDocumentShare,
} from './portalDocumentShares';
import { elementShareHint } from './portalMatrixOrigins';
import { noteSitzGlockePortalAction, sitzGlockeActorFromSeat } from './sitzGlockeStore';

export interface PortalDocumentSharePanelProps {
  doc: PortalProtokoll;
  viewerId: string;
  kanzleiId: string;
  authToken: string;
  canShare: boolean;
  seats: KanzleiSeatForAktePicker[];
  shares: PortalDocumentShare[];
  /** PB matrixRoomId — Element-Deep-Link host-aware; leer = ehrlicher Block. */
  matrixRoomId?: string;
  anchor: { left: number; top: number };
  ignoreCloseRef?: React.RefObject<HTMLElement | null>;
  onClose: () => void;
  onSharesChanged: () => void | Promise<void>;
}

export const PortalDocumentSharePanel: React.FC<PortalDocumentSharePanelProps> = ({
  doc,
  viewerId,
  kanzleiId,
  authToken,
  canShare,
  seats,
  shares,
  matrixRoomId = '',
  anchor,
  ignoreCloseRef,
  onClose,
  onSharesChanged,
}) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const [addId, setAddId] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const docShares = useMemo(
    () => shares.filter((s) => s.document_id === doc.id),
    [doc.id, shares],
  );
  const visibility = describePortalDocumentVisibility({
    viewerId,
    ownerId: doc.customer_id,
    akteId: doc.akte_id,
    shareCount: docShares.length,
  });
  const isOwner = Boolean(viewerId && doc.customer_id && viewerId === doc.customer_id);
  const already = useMemo(() => new Set(docShares.map((s) => s.customer_id)), [docShares]);
  const addable = useMemo(
    () => shareTargetSeats(seats, { viewerId, alreadySharedIds: already }),
    [already, seats, viewerId],
  );
  const elementHint = useMemo(() => elementShareHint(matrixRoomId), [matrixRoomId]);
  const seatById = useMemo(() => {
    const map = new Map<string, KanzleiSeatForAktePicker>();
    for (const s of seats) map.set(s.id, s);
    return map;
  }, [seats]);

  usePopoverDismissOutside({
    open: true,
    onClose,
    rootRef,
    ignoreCloseRef,
    closeOnScroll: false,
  });

  useEffect(() => {
    rootRef.current?.focus();
  }, []);

  const handleAdd = async () => {
    if (!addId || !canShare) return;
    setBusy(true);
    setMessage(null);
    try {
      const result = await createPortalDocumentShare(authToken, {
        document_id: doc.id,
        customer_id: addId,
        kanzlei_id: kanzleiId,
      });
      if (result.ok === false) {
        setMessage(result.error);
        return;
      }
      const actor = sitzGlockeActorFromSeat(seatById.get(viewerId));
      noteSitzGlockePortalAction({
        targetSeatId: addId,
        actorSeatId: viewerId,
        actorName: actor?.name,
        actorRole: actor?.role,
        source: 'dokument',
        refId: doc.id,
        kind: 'added',
      });
      setAddId('');
      await onSharesChanged();
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async (shareId: string) => {
    if (!canShare) return;
    const share = docShares.find((row) => row.id === shareId);
    setBusy(true);
    setMessage(null);
    try {
      const result = await deletePortalDocumentShare(authToken, shareId);
      if (result.ok === false) {
        setMessage(result.error);
        return;
      }
      if (share?.customer_id) {
        const actor = sitzGlockeActorFromSeat(seatById.get(viewerId));
        noteSitzGlockePortalAction({
          targetSeatId: share.customer_id,
          actorSeatId: viewerId,
          actorName: actor?.name,
          actorRole: actor?.role,
          source: 'dokument',
          refId: doc.id,
          kind: 'removed',
        });
      }
      await onSharesChanged();
    } finally {
      setBusy(false);
    }
  };

  if (typeof document === 'undefined') return null;

  return createPortal(
    <div
      ref={rootRef}
      role="dialog"
      aria-modal="false"
      aria-labelledby="portal-doc-share-title"
      tabIndex={-1}
      data-testid="portal-doc-share-popover"
      className="fixed z-[85] rounded-lg border border-violet-500/25 bg-slate-950 p-3 space-y-2 shadow-xl outline-none"
      style={{ left: anchor.left, top: anchor.top, width: `min(${SHARE_POPOVER_WIDTH_PX}px, calc(100vw - 1rem))` }}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p
            id="portal-doc-share-title"
            className="m-0 flex items-center gap-1.5 text-[11px] font-semibold text-violet-100"
          >
            <Share2 className="w-3.5 h-3.5 shrink-0" aria-hidden />
            Teilen · {doc.filename}
          </p>
          <p className="m-0 mt-1 text-[11px] text-slate-300">{visibility.label}</p>
          {elementHint.kind === 'room' && elementHint.url ? (
            <p className="m-0 mt-1">
              <a
                href={elementHint.url}
                target="_blank"
                rel="noreferrer"
                className="text-[11px] text-sky-300 hover:text-sky-200 underline break-all"
                data-testid="portal-doc-share-element-link"
              >
                {elementHint.text}
              </a>
            </p>
          ) : (
            <p className="m-0 mt-1 text-[11px] text-amber-200/80" data-testid="portal-doc-share-element-block">
              {elementHint.text}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded-md p-1 text-slate-500 hover:text-slate-100"
          aria-label="Teilen schließen"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      {docShares.length > 0 ? (
        <ul className="m-0 p-0 list-none space-y-1 max-h-40 overflow-y-auto custom-scrollbar">
          {docShares.map((share) => {
            const seat = seatById.get(share.customer_id);
            return (
              <li
                key={share.id}
                className="flex items-center justify-between gap-2 rounded-md border border-white/[0.06] bg-slate-950/40 px-2 py-1.5"
              >
                <span className="text-[11px] text-slate-200 truncate">
                  {seat ? portalShareSeatLabel(seat) : 'Sitz'}
                  <span className="ml-1 text-slate-500">Lesen</span>
                </span>
                {canShare && isOwner ? (
                  <button
                    type="button"
                    onClick={() => void handleRemove(share.id)}
                    disabled={busy}
                    className="text-[10px] text-slate-400 hover:text-red-200 disabled:opacity-50"
                  >
                    Entfernen
                  </button>
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="m-0 text-[11px] text-slate-500">Keine expliziten Shares.</p>
      )}
      {canShare && isOwner ? (
        <div className="flex flex-col gap-2">
          <select
            value={addId}
            onChange={(e) => setAddId(e.target.value)}
            className="w-full min-w-0 rounded-lg border border-white/[0.08] bg-slate-900/90 px-2 py-1.5 text-xs text-slate-200"
            aria-label="Sitz zum Teilen wählen"
          >
            <option value="">Sitz wählen…</option>
            {addable.map((seat) => (
              <option key={seat.id} value={seat.id}>
                {portalShareSeatLabel(seat)}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => void handleAdd()}
            disabled={busy || !addId}
            className="inline-flex items-center justify-center gap-1 rounded-lg bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-white text-[11px] font-semibold px-3 py-1.5"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Share2 className="w-3.5 h-3.5" />}
            Teilen
          </button>
        </div>
      ) : (
        <p className="m-0 text-[11px] text-slate-500">
          {isOwner
            ? 'Praktikant / Leih: nur lesen — kein Teilen.'
            : 'Nur der Owner-Sitz kann explizit teilen.'}
        </p>
      )}
      {message ? <p className="m-0 text-[11px] text-amber-200">{message}</p> : null}
    </div>,
    document.body,
  );
};
